import React, { useEffect, useRef, useState } from "react";
import { Wheat, Rat } from "lucide-react";
import { Hud, Petunjuk, useCountdown } from "../Hud";
import { PetakSawah, KOLOM, BARIS } from "./PetakSawah";

const DURASI = 60, TARGET = 24, TUMBUH_MS = 3000, HAMA_MS = 2500, TICK = 100;

// Mini game Petani — Sawah: tiap petak bersiklus kosong → ketuk tanam (benih) → ketuk siram (basah, tumbuh ±3 dtk)
// → siap panen (menguning) → ketuk panen (+1 padi); dibiarkan → layu (ketuk untuk bersihkan). Hama hinggap acak di petak
// yang tumbuh/siap — ketuk untuk usir, kalau tidak padi habis. Skor = panen / 24 × 100. Level ↑ → layu lebih cepat, hama lebih sering.
export function Sawah({ shift, onSelesai }) {
  const lv = shift.level || 1;
  const LAYU_MS = Math.max(2500, 4500 - (lv - 1) * 200);
  const ref = useRef(null);
  if (!ref.current) ref.current = { petak: Array.from({ length: KOLOM * BARIS }, () => ({ fase: "kosong", t: 0, hama: null })), panen: 0, usir: 0, selesai: false, nextHama: 0, n: 0, k: 0 };
  const [petak, setPetak] = useState(ref.current.petak);
  const [stat, setStat] = useState({ panen: 0, usir: 0 });
  const [pops, setPops] = useState([]);
  const [perintah, setPerintah] = useState(null); // {i, aksi, k} → petani kecil berjalan & beraksi
  const rnd = () => { const x = Math.sin(shift.seed + (ref.current.n++) * 97.3) * 10000; return x - Math.floor(x); };
  const jedaHama = () => Math.max(2500, 4000 + rnd() * 3000 - (lv - 1) * 250);

  function akhiri() {
    const r = ref.current;
    if (r.selesai) return;
    r.selesai = true;
    onSelesai(Math.min(100, Math.round((r.panen / TARGET) * 100)));
  }
  const sisa = useCountdown(DURASI, akhiri);
  const pop = (i, teks, warna) => setPops(ps => [...ps.slice(-5), { i, teks, warna, k: ref.current.k++ }]);

  useEffect(() => {
    const r = ref.current;
    r.nextHama = performance.now() + 5000;
    const t = setInterval(() => {
      if (r.selesai) return;
      const now = performance.now();
      const P = r.petak.map(p => ({ ...p }));
      P.forEach((p, i) => {
        if (p.hama && now - p.hama > HAMA_MS) { P[i] = { fase: "kosong", t: now, hama: null }; pop(i, "Habis!", "#ffb4b4"); }
        else if (p.fase === "basah" && now - p.t > TUMBUH_MS) { p.fase = "siap"; p.t = now; }
        else if (p.fase === "siap" && now - p.t > LAYU_MS) { p.fase = "layu"; p.t = now; p.hama = null; }
      });
      if (now > r.nextHama) {
        const calon = P.map((p, i) => (!p.hama && (p.fase === "basah" || p.fase === "siap") ? i : -1)).filter(i => i >= 0);
        if (calon.length) P[calon[Math.floor(rnd() * calon.length)]].hama = now;
        r.nextHama = now + jedaHama();
      }
      r.petak = P; setPetak(P);
    }, TICK);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  function tap(i) {
    const r = ref.current;
    if (r.selesai) return;
    const now = performance.now(), P = r.petak.map(p => ({ ...p })), p = P[i];
    let aksi;
    if (p.hama) { p.hama = null; r.usir += 1; pop(i, "Usir!", "#ffd66b"); aksi = "usir"; }
    else if (p.fase === "kosong") { p.fase = "benih"; p.t = now; pop(i, "Tanam"); aksi = "tanam"; }
    else if (p.fase === "benih") { p.fase = "basah"; p.t = now; pop(i, "Siram", "#bfe6ff"); aksi = "siram"; }
    else if (p.fase === "siap") { p.fase = "kosong"; p.t = now; r.panen += 1; pop(i, "+1 Padi", "#ffe08a"); aksi = "panen"; }
    else if (p.fase === "layu") { p.fase = "kosong"; p.t = now; pop(i, "Bersih", "#d9d9d9"); aksi = "bersih"; }
    else return;
    setPerintah({ i, aksi, k: r.k++ });
    r.petak = P; setPetak(P); setStat({ panen: r.panen, usir: r.usir });
  }

  const now = performance.now();
  const tampil = petak.map(p => ({ fase: p.fase, hama: !!p.hama, progres: p.fase === "basah" ? Math.min(1, (now - p.t) / TUMBUH_MS) : 0, peringatan: p.fase === "siap" && LAYU_MS - (now - p.t) < 1200 }));
  const adaHama = petak.some(p => p.hama), adaSiap = petak.some(p => p.fase === "siap"), adaBenih = petak.some(p => p.fase === "benih"), adaLayu = petak.some(p => p.fase === "layu");
  const petunjuk = adaHama ? "Usir hama! Ketuk tikusnya" : adaSiap ? "Panen padi yang menguning" : adaBenih ? "Siram benih yang sudah ditanam" : adaLayu ? "Bersihkan padi yang layu" : "Ketuk petak kosong untuk menanam";

  return (
    <>
      <Hud sisa={sisa} durasi={DURASI} poin={stat.panen} extra={[[Wheat, "#d9a21b", `${stat.panen}/${TARGET}`], [Rat, "#8a5a3c", `${stat.usir}`]]} />
      <PetakSawah petak={tampil} onTap={tap} pops={pops} perintah={perintah} />
      <Petunjuk>{petunjuk}</Petunjuk>
    </>
  );
}
